import { FC, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { ColumnType } from 'antd/lib/table/interface';
import {
  IDropdownOption,
  ILocationsFilters,
  LocationDefault,
  Nullable,
  PaginatedResponse,
  SessionStorageKey,
} from '@common/interfaces';
import { useRepository } from '@context';
import { Button, Main, Page, SearchField, Table, TableFilter, TablePagination } from '@components';
import { FilterIcon, PlusIcon } from '@assets/svg';
import { LocationModal } from './components';
import css from './styles.module.scss';

const DEFAULT_PAGE_SIZE = 10;

const AdminAreaLocationsPage: FC = () => {
  const { accountRepository } = useRepository();

  const params = JSON.parse(sessionStorage.getItem(SessionStorageKey.LOCATIONS_LIST_PARAMS) || '{}') || {};

  const [page, setPage] = useState(params.page || 1);
  const [pageSize, setPageSize] = useState(params.pageSize || DEFAULT_PAGE_SIZE);
  const [order, setOrder] = useState(params.order || '');
  const [search, setSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState<IDropdownOption[]>(params.countryFilter || []);

  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationData, setLocationData] = useState<Nullable<LocationDefault>>(null);

  const {
    data: locations,
    isLoading: isDataLoading,
    refetch: refetchLocations,
  } = useQuery<PaginatedResponse<LocationDefault>>(['locations', order, page, pageSize, search, countryFilter], () =>
    accountRepository.getLocations({
      limit: pageSize,
      offset: (page - 1) * 10,
      ordering: order,
      search,
      country: countryFilter.map(({ value }) => value).join(','),
    })
  );

  const { data: filtersData, isLoading: isFiltersLoading } = useQuery<ILocationsFilters>('locations-filters', () =>
    accountRepository.getLocationsFilters()
  );

  const clearPagination = () => {
    setPage(1);
    setPageSize(DEFAULT_PAGE_SIZE);
    setOrder('');
  };

  const clearFilters = () => {
    setCountryFilter([]);
  };

  const clearAllParams = () => {
    clearPagination();
    clearFilters();
  };

  const handleSearch = (key: string) => {
    clearAllParams();
    setSearch(key);
  };

  useEffect(() => {
    sessionStorage.setItem(
      SessionStorageKey.LOCATIONS_LIST_PARAMS,
      JSON.stringify({
        order,
        page,
        pageSize,
      })
    );
  }, [order, page, pageSize]);

  const columns: ColumnType<LocationDefault>[] = [
    {
      dataIndex: 'country_name',
      key: 'country_name',
      title: 'Country',
      filterIcon: () => <FilterIcon className={`${css.filterIcon} ${countryFilter.length > 0 ? css.active : ''}`} />,
      filterDropdown: ({ confirm }) => (
        <TableFilter
          items={filtersData?.countries}
          selectedItems={countryFilter}
          onChange={(options) => {
            clearPagination();
            setCountryFilter(options);
            confirm();
          }}
        />
      ),
    },
    {
      dataIndex: 'city_name',
      key: 'city_name',
      title: 'Workshop city',
    },
  ];

  const loading = isDataLoading || isFiltersLoading;

  const results = locations?.results || [];

  const isFiltered = countryFilter.length;

  return (
    <Main background='admin'>
      <Page>
        <div className={css.top}>
          <div className={css.title}>Customer Area Management</div>
          <Button
            text='Create new location'
            variant='supernova'
            iconR={<PlusIcon />}
            onClick={() => setShowLocationModal(true)}
          />
        </div>
        <section>
          <div className={css.topPanel}>
            <SearchField
              className={css.search}
              onChange={handleSearch}
              value={search}
              placeholder='Search location...'
            />
            {isFiltered ? <Button text='Reset filters' variant='text' onClick={clearAllParams} /> : null}
          </div>
          <Table
            className={css.table}
            columns={columns}
            data={results}
            loading={loading}
            onChangeColumnOrder={setOrder}
            variant='dark'
            tableLayout='fixed'
            onRowClick={(location: LocationDefault) => {
              setShowLocationModal(true);
              setLocationData(location);
            }}
          />
          <TablePagination
            page={page}
            onChangePage={setPage}
            totalCount={locations?.count}
            onPageSizeChange={setPageSize}
            pageSize={pageSize}
            variant='dark'
          />
        </section>
      </Page>
      {showLocationModal && (
        <LocationModal
          locationData={locationData}
          closeModal={() => {
            setShowLocationModal(false);
            setLocationData(null);
            refetchLocations();
          }}
        />
      )}
    </Main>
  );
};

export default AdminAreaLocationsPage;
